import React, { useContext, useEffect } from 'react';
import { Checkbox, Form, Input, Modal, Select, Space, Typography } from 'antd';

import ServicesContext from '@totem/components/administration/services/ServicesContext';
import { ServiceDefinition } from '@totem/components/administration/services/types';

const { Title } = Typography;

const TOOL_KEY_OPTIONS = ['TEMPERED_ID', 'NinjaOne'].map((key) => ({
  label: key,
  value: key,
}));

const TOOL_API_SITE_TYPE_OPTIONS = ['NinjaOne Location', 'Nozomi Sensor'].map(
  (val) => ({ label: val, value: val }),
);

const toStringArray = (val: unknown): string[] => {
  if (!val) return [];
  if (Array.isArray(val)) return val.filter(Boolean);
  if (typeof val === 'string')
    return val
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean);
  return [];
};

const ServiceEditModal = () => {
  const { editingService, modalOpen, onModalClose, onSave } =
    useContext(ServicesContext);
  const [form] = Form.useForm<ServiceDefinition>();

  const isNew = !editingService?.id;

  useEffect(() => {
    if (modalOpen && editingService) {
      form.setFieldsValue({
        ...editingService,
        aliases: editingService.aliases ?? [],
        includedServices: editingService.includedServices ?? [],
        commissioning: {
          ...editingService.commissioning,
          toolKeys: editingService.commissioning?.toolKeys ?? [],
          toolApiSiteTypes:
            editingService.commissioning?.toolApiSiteTypes ?? [],
        },
      });
    } else if (!modalOpen) {
      form.resetFields();
    }
  }, [modalOpen, editingService, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      onSave({
        ...values,
        id: editingService?.id ?? '',
        aliases: toStringArray(values.aliases),
        includedServices: toStringArray(values.includedServices),
      });
    });
  };

  return (
    <Modal
      title={isNew ? 'Add Service' : 'Edit Service'}
      open={modalOpen}
      onOk={handleOk}
      onCancel={onModalClose}
      width={680}
      okText={isNew ? 'Create' : 'Save'}
      destroyOnClose
    >
      <Form form={form} layout="vertical" size="small">
        <Space style={{ width: '100%' }} direction="vertical" size={0}>
          <Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
            Identity
          </Title>

          <Form.Item
            name="name"
            label="Internal Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="e.g. remote_access" disabled={!isNew} />
          </Form.Item>

          <Form.Item
            name="displayName"
            label="Display Name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="e.g. Remote Access" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item name="aliases" label="Aliases (select or type)">
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Add aliases..."
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Form.Item
            name="includedServices"
            label="Included Services (internal names)"
          >
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="e.g. threat_management"
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
            Commissioning
          </Title>

          <Form.Item
            name={['commissioning', 'requiresDevice']}
            valuePropName="checked"
          >
            <Checkbox>Requires Device</Checkbox>
          </Form.Item>

          <Form.Item
            name={['commissioning', 'requiresBuilding']}
            valuePropName="checked"
          >
            <Checkbox>Requires Building</Checkbox>
          </Form.Item>

          <Form.Item name={['commissioning', 'toolKeys']} label="Tool Keys">
            <Select
              mode="tags"
              options={TOOL_KEY_OPTIONS}
              placeholder="Select or type tool keys..."
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Form.Item
            name={['commissioning', 'toolApiSiteTypes']}
            label="Tool API Site Types"
          >
            <Select
              mode="tags"
              options={TOOL_API_SITE_TYPE_OPTIONS}
              placeholder="Select or type site types..."
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
            Deployment Ticket
          </Title>

          <Form.Item
            name={['deployment', 'createTicket']}
            valuePropName="checked"
          >
            <Checkbox>Create Ticket on Deployment</Checkbox>
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              name={['deployment', 'category']}
              label="Category"
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name={['deployment', 'subCategory']}
              label="Sub-Category"
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input />
            </Form.Item>
          </Space>

          <Title level={5} style={{ marginTop: 8, marginBottom: 4 }}>
            Decommission Ticket
          </Title>

          <Form.Item
            name={['decommission', 'createTicket']}
            valuePropName="checked"
          >
            <Checkbox>Create Ticket on Decommission</Checkbox>
          </Form.Item>

          <Space style={{ width: '100%' }} size="middle">
            <Form.Item
              name={['decommission', 'category']}
              label="Category"
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name={['decommission', 'subCategory']}
              label="Sub-Category"
              style={{ flex: 1, minWidth: 0 }}
            >
              <Input />
            </Form.Item>
          </Space>
        </Space>
      </Form>
    </Modal>
  );
};

export default ServiceEditModal;
